import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Lock, Sparkles, Zap, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/**
 * TreasuryPreview
 * ---------------
 * Marketing preview card for the "Autonomous AI Treasury Protocol" waitlist
 * shipping January 2027. Purely presentational — no backend calls, no writes,
 * no chatbot integration. Renders a dark-glass card with an ambient-purple
 * neon breathing circle, and opens a portalled instructional drawer on click.
 */
const LAUNCH_ISO = "2027-01-19"; // ~139 days from Feb 2026 baseline
const daysUntilLaunch = () => {
  try {
    const now = new Date();
    const then = new Date(LAUNCH_ISO + "T00:00:00Z");
    const diff = Math.max(0, Math.ceil((then - now) / 86400000));
    return diff;
  } catch (_) {
    return 139;
  }
};

export function TreasuryPreview() {
  const [open, setOpen] = useState(false);
  const days = daysUntilLaunch();
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="treasury-preview-card"
        className="relative group rounded-2xl overflow-hidden text-left w-full transition-transform hover:-translate-y-0.5"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.85), rgba(2,6,23,0.95))",
          border: "1px solid rgba(168,85,247,0.42)",
          boxShadow:
            "0 20px 40px -20px rgba(168,85,247,0.55), inset 0 0 30px -20px rgba(168,85,247,0.35)",
          backdropFilter: "blur(14px)",
          minHeight: 180,
        }}
      >
        {/* Ambient purple grain */}
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(600px 200px at 100% 0%, rgba(168,85,247,0.15), transparent 60%), radial-gradient(400px 300px at 0% 100%, rgba(56,189,248,0.08), transparent 70%)",
          }}
        />
        <div className="relative p-5 flex items-start gap-4">
          <div className="flex-shrink-0 relative" style={{ width: 72, height: 72 }}>
            {/* Breathing ambient-purple neon circle */}
            <div className="treasury-orb absolute inset-0 rounded-full" />
            <div
              className="absolute inset-0 rounded-full flex items-center justify-center"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(168,85,247,0.6), rgba(88,28,135,0.4) 60%, rgba(2,6,23,0.9) 100%)",
                border: "1px solid rgba(216,180,254,0.45)",
                boxShadow: "0 0 30px -6px rgba(168,85,247,0.7)",
              }}
            >
              <Lock size={22} className="text-purple-200" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] font-mono uppercase tracking-[0.28em] mb-1.5 flex items-center gap-1.5"
              style={{ color: "#c4b5fd" }}
            >
              <Sparkles size={11} /> Autonomous Treasury · 2027
            </div>
            <div className="text-white text-base font-bold leading-tight tracking-tight mb-2">
              🔒 Autonomous AI Treasury Protocol
              <span className="ml-1 text-purple-300 text-xs font-mono uppercase tracking-widest">
                Enlisting January 2027
              </span>
            </div>
            <div
              data-testid="treasury-preview-countdown"
              className="text-purple-100 text-sm leading-relaxed"
            >
              Projected 2027 Automated Tax Allocation Vault Active In:{" "}
              <span className="font-mono font-bold text-white bg-purple-500/25 px-1.5 py-0.5 rounded">
                {days} Days
              </span>
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-purple-300/80 group-hover:text-purple-200 flex items-center gap-1">
              Tap to preview the 2027 roadmap →
            </div>
          </div>
        </div>
        <style>{`
          .treasury-orb {
            background: radial-gradient(circle, rgba(168,85,247,0.55), transparent 60%);
            filter: blur(14px);
            animation: treasury-breath 3.8s ease-in-out infinite;
          }
          @keyframes treasury-breath {
            0%, 100% { opacity: 0.55; transform: scale(0.92); }
            50%      { opacity: 1;    transform: scale(1.15); }
          }
        `}</style>
      </button>

      {open && createPortal(
        <div
          data-testid="treasury-waitlist-drawer-overlay"
          className="fixed inset-0 z-[9800] flex items-center justify-center px-4 py-6"
          style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="treasury-waitlist-drawer"
            className="relative w-full max-w-lg rounded-2xl overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(30,15,60,0.96))",
              border: "1px solid rgba(168,85,247,0.55)",
              boxShadow:
                "0 30px 60px -18px rgba(168,85,247,0.55), 0 0 60px -20px rgba(56,189,248,0.35)",
              animation: "treasury-drawer-in 0.34s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/25">
              <div className="text-white font-bold text-lg tracking-tight">
                🌟 Master Your 2027 Corporate Capital
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="treasury-waitlist-close"
                aria-label="Close"
                className="text-slate-400 hover:text-white p-1 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-slate-300 text-sm leading-relaxed">
                The Autonomous AI Treasury Protocol is our January 2027 flagship —
                a self-driving allocation engine that shifts capital into
                tax-optimal instruments before your CPA even asks.
              </p>
              <div className="space-y-3">
                {[
                  {
                    icon: TrendingUp,
                    title: "Autonomous Tax Shifting",
                    body:
                      "Live-tuned allocation across accounts to keep your marginal bracket optimized quarter after quarter.",
                    accent: "#a855f7",
                  },
                  {
                    icon: Zap,
                    title: "Algorithmic Invoice Financing",
                    body:
                      "Convert 30/60/90-day A/R into working capital at machine-priced rates — same-hour funding, no paperwork.",
                    accent: "#38bdf8",
                  },
                  {
                    icon: Lock,
                    title: "Zero-Trust Vault Custody",
                    body:
                      "SOC-2 + IRS-compliant vault. Multi-sig, time-locked withdrawals, live audit trails.",
                    accent: "#10b981",
                  },
                ].map((f, i) => (
                  <div
                    key={i}
                    data-testid={`treasury-feature-${i}`}
                    className="flex gap-3 rounded-xl p-3"
                    style={{
                      background: `linear-gradient(135deg, ${f.accent}15, transparent)`,
                      border: `1px solid ${f.accent}33`,
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{
                        background: `${f.accent}22`,
                        border: `1px solid ${f.accent}55`,
                        color: f.accent,
                      }}
                    >
                      <f.icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm leading-tight">
                        {f.title}
                      </div>
                      <div className="text-slate-400 text-xs leading-relaxed mt-0.5">
                        {f.body}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  toast.success("🚀 You're on the 2027 Priority Infrastructure Waitlist", {
                    duration: 4500,
                    description:
                      "We'll ping you the moment the Autonomous Treasury Protocol goes live.",
                  });
                  setOpen(false);
                }}
                data-testid="treasury-waitlist-cta"
                className="w-full mt-2 rounded-lg font-bold text-slate-950 text-sm uppercase tracking-widest transition-transform hover:-translate-y-px"
                style={{
                  padding: "14px 20px",
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  boxShadow:
                    "0 18px 40px -12px rgba(16,185,129,0.7), 0 0 24px -8px rgba(6,182,212,0.55)",
                  letterSpacing: "0.15em",
                }}
              >
                [ Join 2027 Priority Infrastructure Waitlist ]
              </button>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center">
                Preview only · No credit card · Launch estimate: {LAUNCH_ISO}
              </div>
            </div>
            <style>{`
              @keyframes treasury-drawer-in {
                0%   { opacity: 0; transform: translateY(24px) scale(0.96); }
                100% { opacity: 1; transform: translateY(0)    scale(1); }
              }
            `}</style>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
